import { notificationBadge, notificationServerKey } from "../lib/notification-scope";
/**
 * Desktop-only wiring between the shared UI state and the Tauri shell:
 * global shortcuts (push-to-talk, mute, deafen), the system tray (with
 * "Stop sharing screen" while sharing) and the close-to-tray option.
 * Everything here is a no-op in the browser.
 */
import { initGlobalPtt, useGlobalPtt } from "./ptt";
import { tNow } from "../i18n";
import { controller } from "../state/controller";
import { useSettings } from "../state/settings";
import { useScreen } from "../state/screen";
import { useSession, useUi, useVoice } from "../state/stores";
import { isDesktop, tauri, type TauriBridge, type Unlisten } from ".";

type Action = "ptt" | "mute" | "deafen";

/** The global shortcuts that are really registered (the window handlers step aside for those). */
const registered: Record<Action, string | null> = { ptt: null, mute: null, deafen: null };

export function hasGlobalShortcut(action: Action): boolean {
  return action === "ptt" ? ["ready", "legacy"].includes(useGlobalPtt.getState().status) : registered[action] !== null;
}

/** True if this key press is the registered global shortcut for `action` (so the OS already handled it). */
export function isGlobalShortcutEvent(action: Action, e: KeyboardEvent): boolean {
  return registered[action] !== null && registered[action] === acceleratorFromEvent(e);
}

/** KeyboardEvent -> Tauri accelerator (`Control+Shift+KeyM`), or null for bare modifier presses. */
export function acceleratorFromEvent(e: KeyboardEvent): string | null {
  if (/^(Control|Shift|Alt|Meta)(Left|Right)$/.test(e.code)) return null;
  const parts = [e.ctrlKey && "Control", e.altKey && "Alt", e.shiftKey && "Shift", e.metaKey && "Super"].filter(Boolean);
  return [...parts, e.code].join("+");
}

/** "Control+Shift+KeyM" -> ["Ctrl", "Shift", "KeyM"] for display. */
export function acceleratorParts(accelerator: string): string[] {
  return accelerator.split("+").map((p) => (p === "Control" ? "Ctrl" : p === "Super" ? "Cmd/Win" : p));
}

/**
 * Starts the desktop wiring. Returns a cleanup function. Call once from the
 * root component.
 */
export function initDesktop(): (() => void) | undefined {
  if (!isDesktop()) return undefined;
  let disposed = false;
  const cleanups: Unlisten[] = [];
  const cleanup = (fn: Unlisten) => (disposed ? fn() : cleanups.push(fn));

  const register = async (api: TauriBridge, action: Action, accelerator: string | null) => {
    try {
      await api.invoke("set_global_shortcut", { action, accelerator });
      registered[action] = accelerator;
      useUi.getState().dismissToast(`shortcut-${action}`);
    } catch (e) {
      registered[action] = null;
      console.warn(`[desktop] shortcut ${action} ${accelerator}:`, e);
      if (accelerator) useUi.getState().toast("error", tNow("audio.shortcutFailed", { key: accelerator }), { key: `shortcut-${action}`, sticky: true, action: { label: tNow("settings.audio"), run: () => useUi.getState().openSettings("audio") } });
    }
  };

  void (async () => {
    const api = await tauri();
    if (disposed) return;
    cleanup(await initGlobalPtt(api));
    cleanup(await api.listen<{ down: boolean }>("shortcut://ptt", (p) => controller.setPtt(p.down)));
    const act = (action: string) => {
      if (action === "mute") controller.toggleMute();
      else if (action === "deafen") controller.toggleDeafen();
      else if (action === "stop_share") void controller.screen.stopShare();
    };
    cleanup(await api.listen<{ action: string }>("shortcut://action", (p) => act(p.action)));
    cleanup(await api.listen<{ action: string }>("tray://action", (p) => act(p.action)));

    // Settings -> global shortcuts. Only changed values are re-registered.
    const last: Record<Action, string | null | undefined> = { ptt: undefined, mute: undefined, deafen: undefined };
    let lastClose: boolean | undefined;
    const sync = () => {
      const { audio, desktop } = useSettings.getState();
      const wanted: Record<Action, string | null> = {
        ptt: audio.inputMode === "ptt" ? audio.pttKey : null,
        mute: desktop.muteShortcut,
        deafen: desktop.deafenShortcut,
      };
      for (const action of ["mute", "deafen"] as const) {
        if (last[action] === wanted[action]) continue;
        last[action] = wanted[action];
        void register(api, action, wanted[action]);
      }
      if (lastClose !== desktop.closeToTray) {
        lastClose = desktop.closeToTray;
        void api.invoke("set_close_to_tray", { enabled: desktop.closeToTray });
      }
    };
    sync();
    cleanup(useSettings.subscribe(sync));

    // Keep the tray menu in step with the UI.
    const sharing = () => useScreen.getState().own !== null;
    let previousTray = "", previousBadge = -1;
    const pushTray = () => {
      const { muted, deafened } = useVoice.getState();
      const settings = useSettings.getState(), session = useSession.getState();
      const count = Math.min(0xffffffff, notificationBadge(session, settings.notifications, settings.notificationOverrides, notificationServerKey(session)));
      const state = { muted, deafened, sharing: sharing(), language: settings.language, unread: count > 0 };
      const serialized = JSON.stringify(state);
      if (previousTray !== serialized) {
        previousTray = serialized;
        void api.invoke("tray_set_state", state).catch(e => console.warn("[desktop] tray update failed", e));
      }
      if (previousBadge !== count) {
        previousBadge = count;
        void api.invoke("tray_set_badge", { count }).catch(e => console.warn("[desktop] badge update failed", e));
      }
    };
    pushTray();
    cleanup(useSettings.subscribe((s, prev) => s.language !== prev.language || s.notifications !== prev.notifications || s.notificationOverrides !== prev.notificationOverrides ? pushTray() : undefined));
    cleanup(useSession.subscribe((s, prev) => s.threads !== prev.threads || s.address !== prev.address || s.endpoint !== prev.endpoint || s.kind !== prev.kind ? pushTray() : undefined));
    cleanup(useVoice.subscribe((s, prev) => (s.muted !== prev.muted || s.deafened !== prev.deafened ? pushTray() : undefined)));
    // "Stop sharing screen" in the tray, from the picker until the share ends.
    cleanup(useScreen.subscribe((s, prev) => ((s.own !== null) !== (prev.own !== null) ? pushTray() : undefined)));

    await debugAutoconnect(api);
  })();

  return () => {
    disposed = true;
    for (const fn of cleanups.splice(0)) fn();
  };
}

/**
 * Debug builds only: `GWAR_AUTOCONNECT="address|kind"` connects on startup
 * and reports progress to the Rust log, for automated verification.
 */
async function debugAutoconnect(api: TauriBridge): Promise<void> {
  const spec = await api.invoke<string | null>("debug_autoconnect").catch(() => null);
  if (!spec) return;
  const [address = "", kindName = "gwar"] = spec.split("|");
  const kind = kindName === "teamspeak" ? "teamspeak" : "gwar";
  const log = (message: string) => void api.invoke("debug_log", { message }).catch(() => {});

  Object.assign(window, { __vc: { controller, useSettings, useSession, useVoice, useUi, api, log } });
  log(`autoconnect ${address} as ${(await controller.getIdentity()).nickname} (${kind})`);
  useSession.subscribe((s, prev) => {
    if (s.phase !== prev.phase) log(`session phase: ${s.phase}`);
  });
  useVoice.subscribe((s, prev) => {
    if (s.state !== prev.state) log(`voice state: ${s.state}`);
    if (s.micError !== prev.micError) log(`mic error: ${s.micError ? `${s.micError.kind} ${s.micError.message}` : "cleared"}`);
  });
  const ok = await controller.connectInteractive({ kind, address }, { remember: false });
  log(`connect ${ok ? "ok" : "failed"}`);
}
